import JSZip from "jszip";

export const SUPPORTED_CONVERSION_SOURCE_FORMATS = ["TXT", "EPUB", "MOBI", "DOCX", "DOC", "PDF"];
export const SUPPORTED_CONVERSION_TARGET_FORMATS = ["TXT", "EPUB"];

function sanitizeBaseName(fileName, fallbackTitle = "PageTone") {
  const rawName = String(fileName || "").trim().replace(/\.[^.]+$/, "");
  const fallback = String(fallbackTitle || "PageTone").trim() || "PageTone";
  const candidate = rawName || fallback;

  return Array.from(candidate)
    .filter((character) => {
      const codePoint = character.codePointAt(0) || 0;
      return codePoint >= 32 && codePoint !== 127;
    })
    .join("")
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, " ")
    .trim() || fallback;
}

function escapeXml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function normalizeText(text) {
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function splitParagraphs(text) {
  return normalizeText(text)
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function buildTxtContent({ title, chapters, chapterLabel = "Chapter" }) {
  const safeTitle = normalizeText(title);
  const header = safeTitle ? [safeTitle, "=".repeat(Math.max(8, safeTitle.length))] : [];
  const chapterSections = (Array.isArray(chapters) ? chapters : [])
    .map((chapter, index) => {
      const chapterTitle =
        normalizeText(chapter?.title) || `${chapterLabel} ${index + 1}`;
      const chapterText = normalizeText(chapter?.text);

      return [chapterTitle, "", chapterText].filter(Boolean).join("\n");
    })
    .filter(Boolean);

  return [...header, ...(header.length ? [""] : []), ...chapterSections].join("\n\n");
}

function detectPrimaryLanguage(chapters) {
  const sample = (Array.isArray(chapters) ? chapters : [])
    .map((chapter) => String(chapter?.text || ""))
    .join("\n")
    .slice(0, 8000);

  const chineseCount = (sample.match(/[\u3400-\u9fff]/g) || []).length;
  const latinCount = (sample.match(/[A-Za-z]/g) || []).length;

  if (chineseCount > latinCount * 0.4) {
    return "zh";
  }

  return "en";
}

function buildChapterXhtml(chapter, index, chapterLabel = "Chapter") {
  const safeTitle = normalizeText(chapter?.title) || `${chapterLabel} ${index + 1}`;
  const safeParagraphs = splitParagraphs(chapter?.text);
  const paragraphMarkup = safeParagraphs.length
    ? safeParagraphs
        .map((paragraph) =>
          `<p>${escapeXml(paragraph).replace(/\n/g, "<br />")}</p>`
        )
        .join("\n      ")
    : `<p>${escapeXml(normalizeText(chapter?.text || ""))}</p>`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeXml(safeTitle)}</title>
    <link rel="stylesheet" href="styles.css" />
  </head>
  <body>
    <section>
      <h1>${escapeXml(safeTitle)}</h1>
      ${paragraphMarkup}
    </section>
  </body>
</html>`;
}

async function buildEpubBlob({ title, chapters, chapterLabel = "Chapter" }) {
  const zip = new JSZip();
  const safeTitle = normalizeText(title) || "Converted Book";
  const safeChapters = (Array.isArray(chapters) ? chapters : []).filter((chapter) =>
    normalizeText(chapter?.text)
  );
  const bookId = `pagetone-${Date.now().toString(36)}`;
  const primaryLanguage = detectPrimaryLanguage(safeChapters);

  zip.file("mimetype", "application/epub+zip", { compression: "STORE" });
  zip.file(
    "META-INF/container.xml",
    `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`
  );

  zip.file(
    "OEBPS/styles.css",
    `body{font-family:serif;line-height:1.7;margin:5%;}h1{margin-bottom:1.4rem;}p{margin:0 0 1rem;}`
  );

  const chapterItems = safeChapters.map((chapter, index) => {
    const href = `chapter-${index + 1}.xhtml`;
    zip.file(`OEBPS/${href}`, buildChapterXhtml(chapter, index, chapterLabel));
    return {
      id: `chapter-${index + 1}`,
      href,
      title: normalizeText(chapter?.title) || `${chapterLabel} ${index + 1}`
    };
  });

  zip.file(
    "OEBPS/nav.xhtml",
    `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="${primaryLanguage}">
  <head>
    <meta charset="utf-8" />
    <title>${escapeXml(safeTitle)}</title>
  </head>
  <body>
    <nav epub:type="toc" id="toc">
      <h1>${escapeXml(safeTitle)}</h1>
      <ol>
        ${chapterItems
          .map((item) => `<li><a href="${escapeXml(item.href)}">${escapeXml(item.title)}</a></li>`)
          .join("\n        ")}
      </ol>
    </nav>
  </body>
</html>`
  );

  zip.file(
    "OEBPS/content.opf",
    `<?xml version="1.0" encoding="UTF-8"?>
<package version="3.0" xmlns="http://www.idpf.org/2007/opf" unique-identifier="bookid">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="bookid">${escapeXml(bookId)}</dc:identifier>
    <dc:title>${escapeXml(safeTitle)}</dc:title>
    <dc:language>${escapeXml(primaryLanguage)}</dc:language>
  </metadata>
  <manifest>
    <item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>
    <item id="css" href="styles.css" media-type="text/css"/>
    ${chapterItems
      .map((item) => `<item id="${escapeXml(item.id)}" href="${escapeXml(item.href)}" media-type="application/xhtml+xml"/>`)
      .join("\n    ")}
  </manifest>
  <spine>
    ${chapterItems.map((item) => `<itemref idref="${escapeXml(item.id)}"/>`).join("\n    ")}
  </spine>
</package>`
  );

  return zip.generateAsync({
    type: "blob",
    mimeType: "application/epub+zip",
    compression: "DEFLATE",
    compressionOptions: { level: 6 }
  });
}

export async function convertEchoDocument({
  targetFormat,
  title,
  fileName,
  chapters,
  chapterLabel = "Chapter"
}) {
  const normalizedTarget = String(targetFormat || "").toUpperCase();
  const baseName = sanitizeBaseName(fileName, title);

  if (normalizedTarget === "TXT") {
    return {
      blob: new Blob([buildTxtContent({ title, chapters, chapterLabel })], {
        type: "text/plain;charset=utf-8"
      }),
      fileName: `${baseName}.txt`
    };
  }

  if (normalizedTarget === "EPUB") {
    return {
      blob: await buildEpubBlob({ title, chapters, chapterLabel }),
      fileName: `${baseName}.epub`
    };
  }

  throw new Error(`Unsupported conversion target: ${normalizedTarget}`);
}

export async function saveConvertedEchoDocument(blob, fileName) {
  if (typeof window !== "undefined" && typeof window.showSaveFilePicker === "function") {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { saved: true, method: "picker" };
    } catch (error) {
      if (error?.name === "AbortError") {
        return { saved: false, cancelled: true };
      }
    }
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);

  return { saved: true, method: "download" };
}
